/**
 * Feed loader — orchestrates RSS fetching per variant.
 *
 * Features:
 * - Bounded concurrency (semaphore pattern, MAX_CONCURRENT slots)
 * - In-memory cache with TTL
 * - In-flight promise coalescing (1D-1) — concurrent cache misses for the
 *   same feed share a single upstream HTTP request
 * - Negative caching with exponential backoff (1D-3) — failed feeds are
 *   retried at increasing intervals (60s → 120s → … → 30min max) instead
 *   of hammering a dead endpoint every cycle
 * - Variant-switch cancellation
 * - Stale-on-error fallback
 *
 * Pipes results through the data pipeline into appStore.events.
 */

import { getFeedsForVariant, getFeedsForCategories } from '@/config/feeds';
import type { FeedDef } from '@/config/feeds';
import { RssAdapter } from '@/sources/adapters/rss-adapter';
import { processBatch } from '@/core/pipeline';
import { cache } from '@/storage/cache';
import type { NormalizedEvent } from '@/sources/base-source';

const MAX_CONCURRENT = 6;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/** Negative-cache backoff constants. */
const BACKOFF_INITIAL_MS = 60_000;   // 1 minute
const BACKOFF_MAX_MS = 30 * 60_000;  // 30 minutes
const BACKOFF_MULTIPLIER = 2;

interface NegativeCacheEntry {
  failedAt: number;
  backoffMs: number;
  lastError: string;
}

class FeedLoader {
  private activeAbort: AbortController | null = null;
  private adapters = new Map<string, RssAdapter>();

  /**
   * In-flight promise coalescing map.
   * Key = feed ID. While a fetch is in-flight, subsequent requests for the
   * same feed await the existing promise instead of firing a duplicate HTTP
   * request. Entries are cleaned up on settle (success or failure).
   */
  private inFlight = new Map<string, Promise<NormalizedEvent[]>>();

  /**
   * Negative cache — tracks feeds that have recently failed.
   * Key = feed ID. Value = last failure time + current backoff window.
   * A feed inside its backoff window will serve stale data (if available)
   * instead of attempting another upstream request.
   */
  private negativeCache = new Map<string, NegativeCacheEntry>();

  /** Load all feeds for a variant (legacy). Cancels any in-flight fetches first. */
  async loadForVariant(variantId: string): Promise<void> {
    const feeds = getFeedsForVariant(variantId);
    await this.loadFeeds(feeds, `variant: ${variantId}`);
  }

  /** Load feeds matching a set of category names (desk preset mode). */
  async loadForCategories(categories: string[]): Promise<void> {
    const feeds = getFeedsForCategories(categories);
    await this.loadFeeds(feeds, `desk categories: ${categories.length}`);
  }

  /** Internal: load a set of feeds. Cancels any in-flight fetches first. */
  private async loadFeeds(feeds: FeedDef[], label: string): Promise<void> {
    // Cancel previous fetches
    if (this.activeAbort) {
      this.activeAbort.abort();
    }
    this.activeAbort = new AbortController();
    const signal = this.activeAbort.signal;

    if (feeds.length === 0) {
      console.log(`[Sentinel] No feeds configured for ${label}`);
      return;
    }

    // Sort by priority: high first, then normal (default), then low
    const priorityOrder = { high: 0, normal: 1, low: 2 };
    const sorted = feeds.slice().sort((a, b) => {
      return (priorityOrder[a.priority ?? 'normal']) - (priorityOrder[b.priority ?? 'normal']);
    });

    console.log(`[Sentinel] Loading ${sorted.length} feeds for ${label}`);

    // Fetch with bounded concurrency (high-priority feeds fill first slots)
    await this.fetchWithConcurrency(sorted, MAX_CONCURRENT, signal);
  }

  /** Fetch feeds with bounded concurrency (semaphore pattern). */
  private async fetchWithConcurrency(
    feeds: FeedDef[],
    maxConcurrent: number,
    signal: AbortSignal,
  ): Promise<void> {
    const queue = [...feeds];
    const active: Promise<void>[] = [];

    while (queue.length > 0 || active.length > 0) {
      if (signal.aborted) return;

      // Fill up to maxConcurrent
      while (active.length < maxConcurrent && queue.length > 0) {
        const feed = queue.shift()!;
        const task = this.fetchSingleFeed(feed, signal).then(() => {
          const idx = active.indexOf(task);
          if (idx !== -1) active.splice(idx, 1);
        });
        active.push(task);
      }

      // Wait for at least one to finish
      if (active.length > 0) {
        await Promise.race(active);
      }
    }
  }

  /** Fetch a single feed, using cache, coalescing, and negative cache. */
  private async fetchSingleFeed(feed: FeedDef, signal: AbortSignal): Promise<void> {
    if (signal.aborted) return;

    const cacheKey = `feed:${feed.id}`;

    // 1. Positive cache hit — serve immediately
    const cached = cache.get<NormalizedEvent[]>(cacheKey);
    if (cached) {
      await processBatch(cached);
      return;
    }

    // 2. Negative cache check — if we're within the backoff window, serve stale
    if (this.isBackedOff(feed.id)) {
      const stale = cache.getStale<NormalizedEvent[]>(cacheKey);
      if (stale) {
        await processBatch(stale);
      }
      return;
    }

    // 3. In-flight coalescing — if another caller is already fetching this
    //    feed, piggyback on their promise instead of issuing a duplicate request
    const existing = this.inFlight.get(feed.id);
    if (existing) {
      try {
        const events = await existing;
        if (!signal.aborted && events.length > 0) {
          await processBatch(events);
        }
      } catch {
        // The original caller already handled the error path;
        // just serve stale if we can.
        if (!signal.aborted) {
          const stale = cache.getStale<NormalizedEvent[]>(cacheKey);
          if (stale) await processBatch(stale);
        }
      }
      return;
    }

    // 4. No cache, no in-flight — initiate the fetch
    const fetchPromise = this.doFetch(feed);
    this.inFlight.set(feed.id, fetchPromise);

    try {
      const events = await fetchPromise;
      if (signal.aborted) return;

      if (events.length > 0) {
        cache.set(cacheKey, events, CACHE_TTL_MS);
        await processBatch(events);
      }

      // Success clears the negative cache for this feed
      this.negativeCache.delete(feed.id);
    } catch (err) {
      if (signal.aborted) return;

      // Record in negative cache with exponential backoff
      this.recordFailure(feed.id, err);

      // Serve stale on error if available
      const stale = cache.getStale<NormalizedEvent[]>(cacheKey);
      if (stale) {
        await processBatch(stale);
      } else {
        const entry = this.negativeCache.get(feed.id);
        console.warn(
          `[Sentinel] Feed ${feed.id} failed (backoff ${Math.round((entry?.backoffMs ?? 0) / 1000)}s):`,
          err,
        );
      }
    } finally {
      this.inFlight.delete(feed.id);
    }
  }

  /**
   * Perform the actual upstream fetch for a feed.
   * Separated so the in-flight promise map stores only the HTTP work,
   * not the pipeline/cache logic.
   */
  private async doFetch(feed: FeedDef): Promise<NormalizedEvent[]> {
    let adapter = this.adapters.get(feed.id);
    if (!adapter) {
      adapter = new RssAdapter({
        id: feed.id,
        name: feed.name,
        feedUrl: feed.url,
        category: feed.category,
        language: feed.language,
      });
      await adapter.initialize();
      this.adapters.set(feed.id, adapter);
    }
    return adapter.fetch();
  }

  // --- Negative cache helpers ---

  /** Check if a feed is currently in its backoff window. */
  private isBackedOff(feedId: string): boolean {
    const entry = this.negativeCache.get(feedId);
    if (!entry) return false;

    const elapsed = Date.now() - entry.failedAt;
    if (elapsed >= entry.backoffMs) {
      // Backoff window expired — allow a retry
      return false;
    }
    return true;
  }

  /** Record a fetch failure with exponential backoff. */
  private recordFailure(feedId: string, err: unknown): void {
    const existing = this.negativeCache.get(feedId);
    const errorMsg = err instanceof Error ? err.message : String(err);

    if (existing) {
      // Double the backoff, capped at max
      this.negativeCache.set(feedId, {
        failedAt: Date.now(),
        backoffMs: Math.min(existing.backoffMs * BACKOFF_MULTIPLIER, BACKOFF_MAX_MS),
        lastError: errorMsg,
      });
    } else {
      // First failure — start at initial backoff
      this.negativeCache.set(feedId, {
        failedAt: Date.now(),
        backoffMs: BACKOFF_INITIAL_MS,
        lastError: errorMsg,
      });
    }
  }

  /** Stop all in-flight fetches. */
  stop(): void {
    if (this.activeAbort) {
      this.activeAbort.abort();
      this.activeAbort = null;
    }
  }

  /** Get diagnostics about the negative cache (for settings/debug panel). */
  getNegativeCacheStatus(): Map<string, { backoffMs: number; lastError: string; retriesIn: number }> {
    const result = new Map<string, { backoffMs: number; lastError: string; retriesIn: number }>();
    const now = Date.now();
    for (const [feedId, entry] of this.negativeCache) {
      const elapsed = now - entry.failedAt;
      const retriesIn = Math.max(0, entry.backoffMs - elapsed);
      result.set(feedId, {
        backoffMs: entry.backoffMs,
        lastError: entry.lastError,
        retriesIn,
      });
    }
    return result;
  }
}

/** Singleton feed loader. */
export const feedLoader = new FeedLoader();
